import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Check, X, Copy, MessageCircle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FRIGATES = ["Cherokee", "Cumberland", "Pelican"];
const STATUS = { pending: "bg-amber-500/15 text-amber-500", approved: "bg-emerald-500/15 text-emerald-500", declined: "bg-muted text-muted-foreground" };

// Sign-up requests from brothers without a code — officers approve (→ invite link) or decline
export default function AccessRequests({ onInviteCreated }) {
  const { user } = useAuth();
  const admin = !!user?.is_galleon_admin;
  const [items, setItems] = useState([]);
  const [frig, setFrig] = useState({});
  const [busy, setBusy] = useState("");
  const [links, setLinks] = useState({});
  const load = () => api.get("/admin/access-requests").then((r) => setItems(r.data.requests)).catch(() => {});
  useEffect(() => { load(); }, []);

  const approve = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/admin/access-requests/${r.id}/approve`, { frigate: frig[r.id] || r.frigate || null });
      setLinks((l) => ({ ...l, [r.id]: data.invite })); toast.success(`Approved — share the link with ${r.name.split(" ")[0]}`); load(); onInviteCreated?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const decline = async (r) => {
    if (!window.confirm(`Decline ${r.name}'s request?`)) return;
    setBusy(r.id);
    try { await api.post(`/admin/access-requests/${r.id}/decline`, {}); toast.success("Request declined"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.name}'s ${r.status} request from the list?`)) return;
    setBusy(r.id);
    try { await api.delete(`/admin/access-requests/${r.id}`); toast.success("Request removed"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const wa = (r, inv) => `https://wa.me/?text=${encodeURIComponent(`Alora ${r.name.split(" ")[0]}! Your request to board the IOTA PRIVATE VESSEL is approved. Register here: ${inv.link}  (code ${inv.code})`)}`;
  const pending = items.filter((i) => i.status === "pending");

  return (
    <Card className="gold-ring glass overflow-hidden lg:col-span-3" data-testid="access-requests-card">
      <div className="flex items-center gap-2 border-b border-border/60 p-4"><UserPlus className="h-5 w-5 text-[hsl(var(--water))]" /><h3 className="font-display text-lg font-700">Sign-up requests</h3>{pending.length > 0 && <Badge className="bg-amber-500 text-white" data-testid="access-requests-pending">{pending.length} pending</Badge>}</div>
      <ul className="divide-y divide-border/60">
        {items.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground" data-testid="access-requests-empty">No sign-up requests yet. Brothers without a code can ask from the Sign up page.</li>}
        {items.map((r) => { const inv = links[r.id]; return (
          <li key={r.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`access-request-${r.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-700">{r.name}{r.deck_name && <span className="text-muted-foreground"> “{r.deck_name}”</span>} <span className={`ml-2 rounded-full px-2 py-0.5 text-[0.6rem] font-700 uppercase tracking-wider ${STATUS[r.status]}`} data-testid={`access-request-status-${r.id}`}>{r.status}</span></p>
              <p className="text-xs text-muted-foreground">{r.email}{r.phone ? ` · ${r.phone}` : ""}{r.frigate ? ` · ${r.frigate}` : ""}{r.note ? ` · vouched: ${r.note}` : ""} · {new Date(r.created_at).toLocaleDateString()}</p>
              {r.status !== "pending" && <p className="text-[0.65rem] text-muted-foreground">{r.status} by {r.decided_by}{r.invite_code ? ` · code ${r.invite_code}` : ""}</p>}
              {inv && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-[hsl(var(--water))]/40 bg-[hsl(var(--water))]/10 p-2" data-testid={`access-request-link-${r.id}`}>
                  <span className="font-mono-data text-xs">{inv.code}</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { navigator.clipboard.writeText(inv.link); toast.success("Link copied"); }} data-testid={`access-request-copy-${r.id}`}><Copy className="mr-1 h-3 w-3" />Copy link</Button>
                  <a href={wa(r, inv)} target="_blank" rel="noopener noreferrer"><Button size="sm" className="h-7 bg-[#25D366] text-xs font-700 text-white hover:bg-[#1da851]" data-testid={`access-request-wa-${r.id}`}><MessageCircle className="mr-1 h-3 w-3" />WhatsApp</Button></a>
                </div>
              )}
            </div>
            {r.status !== "pending" && (
              <Button size="icon" variant="ghost" onClick={() => remove(r)} disabled={busy === r.id} className="h-8 w-8 text-muted-foreground hover:text-destructive" title="Remove from list" data-testid={`access-request-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
            )}
            {r.status === "pending" && (
              <div className="flex items-center gap-2">
                {admin && (
                  <Select value={frig[r.id] || r.frigate || ""} onValueChange={(v) => setFrig({ ...frig, [r.id]: v })}>
                    <SelectTrigger className="h-8 w-36 text-xs" data-testid={`access-request-frigate-${r.id}`}><SelectValue placeholder="Frigate" /></SelectTrigger>
                    <SelectContent>{FRIGATES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <Button size="sm" onClick={() => approve(r)} disabled={busy === r.id} className="h-8 bg-emerald-600 text-xs font-700 text-white hover:bg-emerald-700" data-testid={`access-request-approve-${r.id}`}>{busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="mr-1 h-3 w-3" />Approve</>}</Button>
                <Button size="sm" variant="ghost" onClick={() => decline(r)} disabled={busy === r.id} className="h-8 text-xs text-destructive" data-testid={`access-request-decline-${r.id}`}><X className="mr-1 h-3 w-3" />Decline</Button>
              </div>
            )}
          </li>); })}
      </ul>
    </Card>
  );
}
